import { useEffect, useMemo, useState } from 'react';

import { useLazyGetStocksQuery } from '@/api/stockQuery';
import type { IStockData } from '@/common/interfaces/stocks.interface';
import { getStockColorCodeCandidates } from '@/utilities/stockUtils';

interface IUseStocksWithColorFallbackParams {
  styleCode: string;
  apparelColorCode?: string;
  apparelColorHex?: string;
  skip?: boolean;
}

export const useStocksWithColorFallback = ({
  styleCode,
  apparelColorCode = '',
  apparelColorHex = '',
  skip = false,
}: IUseStocksWithColorFallbackParams) => {
  const [fetchStocks] = useLazyGetStocksQuery();
  const [stockData, setStockData] = useState<IStockData[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const colorCandidates = useMemo(
    () => getStockColorCodeCandidates(apparelColorCode, apparelColorHex),
    [apparelColorCode, apparelColorHex]
  );

  useEffect(() => {
    if (skip || !styleCode.trim()) {
      setStockData([]);
      return undefined;
    }

    let cancelled = false;
    setIsLoading(true);

    const fetchStock = async () => {
      const resolvedStockData = await colorCandidates.reduce<Promise<IStockData[] | null>>(
        async (accPromise, colorCode) => {
          const acc = await accPromise;
          if (acc) {
            return acc;
          }

          try {
            const result = await fetchStocks({ styleCode, colorCode }).unwrap();
            if (result?.data?.stock_data?.length) {
              return result.data.stock_data;
            }
          } catch {
            /* try next color code candidate */
          }

          return null;
        },
        Promise.resolve(null)
      );

      if (!cancelled) {
        setStockData(resolvedStockData ?? []);
      }
    };

    fetchStock()
      .catch(() => {
        if (!cancelled) {
          setStockData([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [colorCandidates, fetchStocks, skip, styleCode]);

  return { stockData, isLoading };
};
